import React, { useState } from 'react';
import { Crown } from 'lucide-react';
import { Leadership } from '../../types/Leadership';
import { apiClient } from '../../config/api';
import AdminTable from '../shared/AdminTable';
import AdminHeader from '../shared/AdminHeader';
import LeadershipForm, { LeadershipFormData } from './LeadershipForm';
import { leadershipTableConfig } from './LeadershipTableConfig';

const EnhancedLeadershipManagement: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingLeadership, setEditingLeadership] = useState<Leadership | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const actions = {
    handleCreate: () => {
      setEditingLeadership(null);
      setShowForm(true);
    },

    handleEdit: (leadership: Leadership) => {
      setEditingLeadership(leadership);
      setShowForm(true);
    },

    handleView: (leadership: Leadership) => {
      // For now, view is the same as edit
      // In the future, you could implement a read-only view
      setEditingLeadership(leadership);
      setShowForm(true);
    }
  };

  const handleFormSubmit = async (formData: LeadershipFormData): Promise<Leadership> => {
    try {
      let result: any;
      if (editingLeadership) {
        result = await apiClient.put(`/api/admin/leadership/${editingLeadership.id}`, formData);
      } else {
        result = await apiClient.post('/api/admin/leadership', formData);
      }

      // Ensure we have a valid Leadership object
      if (!result || !result.id) {
        throw new Error('Invalid response from server');
      }

      setShowForm(false);
      setEditingLeadership(null);
      setRefreshTrigger(prev => prev + 1);

      return result as Leadership;
    } catch (error) {
      console.error('Error saving leadership:', error);
      throw new Error('Failed to save leadership');
    }
  };

  const handleDelete = async (leadershipId: string) => {
    if (!window.confirm('Are you sure you want to delete this leadership member?')) return;

    try {
      await apiClient.delete(`/api/admin/leadership/${leadershipId}`);
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Failed to delete leadership:', error);
      alert('Failed to delete leadership');
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingLeadership(null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-8 space-y-6">
        {/* Header */}
        <AdminHeader
          title="Manage Leadership"
          icon={<Crown size={32} />}
          subtitle="Manage leadership team members and their roles"
        />

        <div className="flex justify-end">
          <button
            onClick={actions.handleCreate}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center gap-2"
          >
            <Crown size={20} />
            Add New Leadership Member
          </button>
        </div>

        {/* Main Content */}
        {showForm ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingLeadership ? 'Edit Leadership Member' : 'Add New Leadership Member'}
              </h2>
            </div>
            <div className="p-6">
              <LeadershipForm
                leadership={editingLeadership}
                onSubmit={handleFormSubmit}
                onCancel={handleFormCancel}
              />
            </div>
          </div>
        ) : (
          <AdminTable
            onView={actions.handleView}
            onEdit={actions.handleEdit}
            onDelete={handleDelete}
            config={leadershipTableConfig}
            refreshTrigger={refreshTrigger}
          />
        )}
      </div>
    </div>
  );
};

export default EnhancedLeadershipManagement;
